import { useEffect, useMemo, useState } from 'react';
import type { UserClaims } from '@okta/okta-auth-js';
import { useAuth } from '../auth/useAuth';
import { ProjectType } from '../api/sophia';
import { getUserDisplayName, getUserInitials } from '../utils/userInfo';

interface SidebarProps {
  selectedCount: number;
  onOpenSelection: () => void;
  onOpenStorage: () => void;
  onOpenNewProject: () => void;
  onOpenProfile: () => void;
  onOpenFeedback: () => void;
  onOpenAdmin?: () => void;
  isAdmin?: boolean;
  hasAdminNotification?: boolean;
  adminNotificationItems?: string[];
  isAdminMode?: boolean;
  activeAdminPage?: 'mappings' | 'forecast';
  onOpenAdminMappings?: () => void;
  onOpenAdminForecast?: () => void;
  onExitAdmin?: () => void;
  projectType?: ProjectType;
  onExportExcel?: () => void;
  onExportWeborder?: () => void;
  canExport?: boolean;
}

type OktaAuthWithGetUser = {
  getUser: () => Promise<UserClaims>;
};

function hasGetUser(auth: unknown): auth is OktaAuthWithGetUser {
  return Boolean(auth) && typeof (auth as OktaAuthWithGetUser).getUser === 'function';
}

export function Sidebar({
  selectedCount,
  onOpenSelection,
  onOpenStorage,
  onOpenNewProject,
  onOpenProfile,
  onOpenFeedback,
  onOpenAdmin,
  isAdmin,
  hasAdminNotification,
  adminNotificationItems,
  isAdminMode,
  activeAdminPage,
  onOpenAdminMappings,
  onOpenAdminForecast,
  onExitAdmin,
  projectType,
  onExportExcel,
  onExportWeborder,
  canExport,
}: SidebarProps) {
  const { authState, oktaAuth } = useAuth();
    
  const [user, setUser] = useState<UserClaims | null>(null);

  useEffect(() => {
    let isMounted = true;

    if (!authState?.isAuthenticated) {
      setUser(null);
      return () => {
        isMounted = false;
      };
    }

    // Use the unified getUser method that handles debug mode transparently
    if (hasGetUser(oktaAuth)) {
      oktaAuth
        .getUser()
        .then((info: UserClaims) => {
          if (isMounted) {
            setUser(info);
          }
        })
        .catch((err: unknown) => {
          console.error('Failed to get user info:', err);
          if (isMounted) {
            setUser(null);
          }
        });
    }

    return () => {
      isMounted = false;
    };
  }, [authState, oktaAuth]);

  const displayName = useMemo(() => {
    return getUserDisplayName(user, 'Test User');
  }, [user]);

  const initials = useMemo(() => getUserInitials(user), [user]);

  if (isAdminMode) {
    return (
      <aside className="fixed right-0 top-0 z-20 flex h-full w-28 flex-col gap-3 border-l border-gray-200 bg-neutral-50 p-2 py-4">
        <div className="px-1 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-700">Admin</div>
        <button
          type="button"
          onClick={onOpenAdminMappings}
          className={`flex max-w-full flex-col items-center gap-2 rounded-lg p-2 text-center transition-colors duration-200 ${
            activeAdminPage === 'mappings' ? 'bg-gray-200' : 'bg-transparent hover:bg-gray-200'
          }`}
        >
          <svg className="mb-2 h-8 w-8 text-black" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
            <path d="M3 10H21M9 10V20M15 10V20" stroke="currentColor" strokeWidth="2" />
          </svg>
          <span className="text-xs font-semibold leading-tight text-black">MAPPINGS</span>
        </button>
        <button
          type="button"
          onClick={onOpenAdminForecast}
          className={`flex max-w-full flex-col items-center gap-2 rounded-lg p-2 text-center transition-colors duration-200 ${
            activeAdminPage === 'forecast' ? 'bg-gray-200' : 'bg-transparent hover:bg-gray-200'
          }`}
        >
          <svg className="mb-2 h-8 w-8 text-black" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M13 2L5 13H11L9 22L19 9H13L13 2Z"
              fill="currentColor"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-xs font-semibold leading-tight text-black">FORECAST</span>
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onExitAdmin}
          className="flex max-w-full flex-col items-center gap-2 rounded-lg bg-transparent p-2 text-center transition-colors duration-200 hover:bg-gray-200"
        >
          <i className="ti-arrow-left mb-2 text-3xl text-black" aria-hidden="true" />
          <span className="text-xs font-semibold leading-tight text-black">TORNA ALL'APP</span>
        </button>
        <button
          type="button"
          onClick={onOpenProfile}
          className="flex max-w-full flex-col items-center gap-2 rounded-lg bg-transparent p-2 text-center transition-colors duration-200 hover:bg-gray-200"
        >
          <img
            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}`}
            alt={displayName}
            className="mb-2 w-16 rounded-full"
          />
          <span className="text-xs font-semibold leading-tight text-black">{displayName}</span>
        </button>
      </aside>
    );
  }

  return (
    <aside className="fixed right-0 top-0 z-20 flex h-full w-28 flex-col gap-3 border-l border-gray-200 bg-neutral-50 p-2 py-4">
      {projectType !== 'optimization' && (
        <button
          type="button"
          onClick={onOpenSelection}
          className="flex max-w-full flex-col items-center gap-2 rounded-lg bg-transparent p-3 text-center transition-colors duration-200 hover:bg-gray-200"
        >
          <i className="ti-folder mb-2 text-3xl text-black" aria-hidden="true" />
          <span className="text-xs font-semibold leading-tight text-black">
            {selectedCount} PRODOTTI SELEZIONATI
          </span>
        </button>
      )}
      <button
        type="button"
        onClick={onOpenStorage}
        className="flex max-w-full flex-col items-center gap-2 rounded-lg bg-transparent p-2 text-center transition-colors duration-200 hover:bg-gray-200"
      >
        <i className="ti-save mb-2 text-3xl text-black" aria-hidden="true" />
        <span className="text-xs font-semibold leading-tight text-black">CARICA PROGETTO</span>
      </button>
      <button
        type="button"
        onClick={onOpenNewProject}
        className="flex max-w-full flex-col items-center gap-2 rounded-lg bg-transparent p-2 text-center transition-colors duration-200 hover:bg-gray-200"
      >
        <i className="ti-plus mb-2 text-3xl text-black" aria-hidden="true" />
        <span className="text-xs font-semibold leading-tight text-black">NUOVO PROGETTO</span>
      </button>
      {projectType && (
        <button
          type="button"
          onClick={onOpenFeedback}
          className="flex max-w-full flex-col items-center gap-2 rounded-lg bg-transparent p-2 text-center transition-colors duration-200 hover:bg-gray-200"
        >
          <i className="ti-flag mb-2 text-3xl text-black" aria-hidden="true" />
          <span className="text-xs font-semibold leading-tight text-black">FEEDBACK</span>
        </button>
      )}
      {isAdmin && onOpenAdmin && (
        <button
          type="button"
          onClick={onOpenAdmin}
          className="group relative flex max-w-full flex-col items-center gap-2 rounded-lg bg-transparent p-2 text-center transition-colors duration-200 hover:bg-gray-200"
        >
          {hasAdminNotification && (
            <span className="absolute right-2 top-1 inline-flex min-h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white" aria-hidden="true">
              {Math.max(1, adminNotificationItems?.length ?? 0)}
            </span>
          )}
          <i className="ti-shield mb-2 text-3xl text-black" aria-hidden="true" />
          <span className="text-xs font-semibold leading-tight text-black">ADMIN</span>
          {hasAdminNotification && (adminNotificationItems?.length ?? 0) > 0 && (
            <div className="pointer-events-none absolute right-full top-1/2 z-30 mr-2 hidden w-64 -translate-y-1/2 rounded border border-red-200 bg-white p-2 text-left shadow-lg group-hover:block">
              <div className="mb-1 text-xs font-semibold text-red-700">Notifiche Admin</div>
              <div className="flex flex-col gap-1">
                {adminNotificationItems?.map((item, idx) => (
                  <div key={`${item}-${idx}`} className="text-xs text-gray-700">
                    - {item}
                  </div>
                ))}
              </div>
            </div>
          )}
        </button>
      )}
      {/* Export buttons - only for optimization projects with valid data */}
      {projectType === 'optimization' && canExport && (
        <>
          <div className="border-t border-gray-300 my-2" />
          <button
            type="button"
            onClick={onExportExcel}
            className="flex max-w-full flex-col items-center gap-2 rounded-lg p-2 text-center transition-colors duration-200 hover:opacity-80"
            style={{ backgroundColor: 'rgba(34, 139, 34, 0.25)' }}
          >
            <svg className="h-8 w-8 text-black" viewBox="0 0 192 192" xmlns="http://www.w3.org/2000/svg" fill="none">
              <path d="M56 30c0-1.662 1.338-3 3-3h108c1.662 0 3 1.338 3 3v132c0 1.662-1.338 3-3 3H59c-1.662 0-3-1.338-3-3v-32m0-68V30" style={{stroke: 'currentColor', strokeWidth: 12, strokeLinecap: 'round'}}/>
              <rect width="68" height="68" x="22" y="62" rx="3" style={{stroke: 'currentColor', strokeWidth: 12, strokeLinecap: 'round', fill: 'none'}}/>
              <path d="M138.79 164.725V27.175M56.175 58.792H170M170 96H90.328M169 133.21H56.175M44.5 82l23 28m0-28-23 28" style={{stroke: 'currentColor', strokeWidth: 12, strokeLinecap: 'round', strokeLinejoin: 'round', fill: 'none'}}/>
            </svg>
            <span className="text-xs font-semibold leading-tight text-black">ESPORTA EXCEL</span>
          </button>
          <button
            type="button"
            onClick={onExportWeborder}
            className="flex max-w-full flex-col items-center gap-2 rounded-lg p-2 text-center transition-colors duration-200 hover:opacity-80"
            style={{ backgroundColor: 'rgba(206, 154, 133, 0.4)' }}
          >
            <svg className="h-8 w-8 text-black" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="text-xs font-semibold leading-tight text-black">ESPORTA WEBORDER</span>
          </button>
        </>
      )}
      <div className="flex-1" />
      <button
        type="button"
        onClick={onOpenProfile}
        className="flex max-w-full flex-col items-center gap-2 rounded-lg bg-transparent p-2 text-center transition-colors duration-200 hover:bg-gray-200"
      >
        <img
          src={`https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}`}
          alt={displayName}
          className="mb-2 w-16 rounded-full"
        />
        <span className="text-xs font-semibold leading-tight text-black">{displayName}</span>
      </button>
    </aside>
  );
}

export default Sidebar;
